'use client';

import { useEffect, useRef, useCallback, useState } from 'react';

type PullState = 'idle' | 'pulling' | 'refreshing';

const THRESHOLD = 60;

/**
 * Generic over the element it attaches to. It was fixed to `HTMLElement`, which
 * happened to satisfy a `<main>` but not a `<div>` — React's `Ref<T>` is
 * invariant, so the wider type is not assignable to the narrower one. Callers
 * name their element (`usePullToRefresh<HTMLDivElement>(…)`) and the ref
 * type-checks against the tag they actually render.
 */
export function usePullToRefresh<T extends HTMLElement = HTMLElement>(
  onRefresh: () => void | Promise<void>,
) {
  const [state, setState] = useState<PullState>('idle');
  const [pullDistance, setPullDistance] = useState(0);
  const startY = useRef(0);
  const containerRef = useRef<T | null>(null);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    const el = e.currentTarget as HTMLElement;
    if (el.scrollTop > 0) return;
    startY.current = e.touches[0].clientY;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const el = e.currentTarget as HTMLElement;
    if (el.scrollTop > 0) return;
    const deltaY = Math.max(0, e.touches[0].clientY - startY.current);
    setPullDistance(deltaY);
    if (deltaY > THRESHOLD) {
      setState('pulling');
    } else {
      setState('idle');
    }
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (state === 'pulling') {
      setState('refreshing');
      setPullDistance(THRESHOLD);
      await onRefresh();
      setState('idle');
      setPullDistance(0);
    } else {
      setPullDistance(0);
    }
  }, [state, onRefresh]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  return { containerRef, state, pullDistance };
}
